<template>
  <div class="tasks-list-view">
    <b-card>
      <!-- Filters -->
      <b-row class="mb-2">
        <b-col md="4">
          <b-form-input
            v-model="localFilter.search"
            placeholder="البحث بالاسم..."
            @input="onSearchChange"
          />
        </b-col>
        <b-col md="3">
          <v-select
            v-model="localFilter.status"
            :options="statusOptions"
            :reduce="option => option.value"
            label="label"
            placeholder="الحالة"
            :clearable="true"
            @input="onFilterChange"
          />
        </b-col>
        <b-col md="3">
          <v-select
            v-model="localFilter.priority"
            :options="priorityOptions"
            :reduce="option => option.value"
            label="label"
            placeholder="الأولوية"
            :clearable="true"
            @input="onFilterChange"
          />
        </b-col>
        <b-col
          md="2"
          class="text-right"
        >
          <b-button
            variant="outline-secondary"
            @click="clearFilters"
          >
            <feather-icon
              icon="RefreshCwIcon"
              class="mr-50"
            />
            إعادة تعيين
          </b-button>
        </b-col>
      </b-row>

      <!-- Loading State -->
      <center v-if="loading">
        <b-spinner
          variant="primary"
          label="جاري التحميل..."
        />
      </center>

      <!-- Tasks Table -->
      <div v-else>
        <b-table
          :items="filteredTasks"
          :fields="fields"
          striped
          responsive
          hover
          :per-page="perPage"
          :current-page="currentPage"
          show-empty
          empty-text="لا توجد مهام"
        >
          <!-- Status -->
          <template #cell(status)="data">
            <b-badge
              :variant="getStatusVariant(data.item.status)"
              pill
            >
              {{ getStatusLabel(data.item.status) }}
            </b-badge>
          </template>

          <!-- Priority -->
          <template #cell(priority)="data">
            <b-badge
              v-if="data.item.priority"
              :variant="getPriorityVariant(data.item.priority)"
            >
              {{ data.item.priority }}
            </b-badge>
            <span
              v-else
              class="text-muted"
            >-</span>
          </template>

          <!-- Due Date -->
          <template #cell(due_date)="data">
            <span v-if="data.item.due_date">
              {{ data.item.due_date | formatDate }}
            </span>
            <span
              v-else
              class="text-muted"
            >-</span>
          </template>

          <!-- Assignee -->
          <template #cell(assignee)="data">
            <div
              v-if="data.item.assignee"
              class="d-flex align-items-center"
            >
              <b-avatar
                :src="data.item.assignee.avatar"
                :text="avatarText(data.item.assignee.name)"
                size="32"
                variant="light-primary"
                class="mr-50"
              />
              <span>{{ data.item.assignee.name }}</span>
            </div>
            <span
              v-else
              class="text-muted"
            >غير محدد</span>
          </template>

          <!-- Actions -->
          <template #cell(actions)="data">
            <div class="d-flex align-items-center">
              <b-link
                :to="{ name: 'taskDetails', params: { id: data.item.id } }"
                class="mr-1"
              >
                <feather-icon
                  icon="EyeIcon"
                  class="text-primary"
                  size="20"
                />
              </b-link>

              <b-dropdown
                variant="link"
                no-caret
                toggle-class="p-0"
                right
              >
                <template #button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="20"
                    class="text-muted"
                  />
                </template>

                <b-dropdown-item @click="changeStatus(data.item, 'pending')">
                  <feather-icon
                    icon="ClockIcon"
                    size="16"
                    class="mr-50"
                  />
                  <span>قيد الانتظار</span>
                </b-dropdown-item>

                <b-dropdown-item @click="changeStatus(data.item, 'in_progress')">
                  <feather-icon
                    icon="PlayIcon"
                    size="16"
                    class="mr-50"
                  />
                  <span>قيد التنفيذ</span>
                </b-dropdown-item>

                <b-dropdown-item @click="changeStatus(data.item, 'review')">
                  <feather-icon
                    icon="EyeIcon"
                    size="16"
                    class="mr-50"
                  />
                  <span>قيد المراجعة</span>
                </b-dropdown-item>

                <b-dropdown-item @click="changeStatus(data.item, 'completed')">
                  <feather-icon
                    icon="CheckIcon"
                    size="16"
                    class="mr-50"
                  />
                  <span>مكتملة</span>
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </template>
        </b-table>

        <!-- Pagination -->
        <b-pagination
          v-if="totalRows > perPage"
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="center"
          class="mt-2"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BBadge,
  BAvatar,
  BLink,
  BButton,
  BSpinner,
  BPagination,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { avatarText } from '@core/utils/filter'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'TasksListView',
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BTable,
    BBadge,
    BAvatar,
    BLink,
    BButton,
    BSpinner,
    BPagination,
    BDropdown,
    BDropdownItem,
    vSelect,
  },
  filters: {
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },
  },
  props: {
    tasks: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentPage: 1,
      perPage: 10,
      localFilter: {
        search: '',
        status: null,
        priority: null,
      },
      fields: [
        {
          key: 'project_service_action.name', label: 'اسم المهمة', sortable: true, thClass: 'text-right',
        },
        {
          key: 'status', label: 'الحالة', sortable: true, thClass: 'text-center', tdClass: 'text-center',
        },
        {
          key: 'priority', label: 'الأولوية', sortable: true, thClass: 'text-center', tdClass: 'text-center',
        },
        { key: 'due_date', label: 'تاريخ التسليم', sortable: true },
        { key: 'assigned_user.name', label: 'المسؤول' },
        {
          key: 'actions', label: 'الإجراءات', thClass: 'text-center', tdClass: 'text-center',
        },
      ],
      statusOptions: [
        { value: 'pending', label: 'قيد الانتظار' },
        { value: 'in_progress', label: 'قيد التنفيذ' },
        { value: 'review', label: 'قيد المراجعة' },
        { value: 'completed', label: 'مكتملة' },
      ],
      priorityOptions: [
        { value: 'low', label: 'منخفضة' },
        { value: 'medium', label: 'متوسطة' },
        { value: 'high', label: 'عالية' },
        { value: 'urgent', label: 'عاجلة' },
      ],
    }
  },
  computed: {
    filteredTasks() {
      let filtered = [...this.tasks]

      // Search filter
      if (this.localFilter.search) {
        const search = this.localFilter.search.toLowerCase()
        filtered = filtered.filter(task => (task.name && task.name.toLowerCase().includes(search))
          || (task.title && task.title.toLowerCase().includes(search))
          || (task.description && task.description.toLowerCase().includes(search)))
      }

      // Status filter
      if (this.localFilter.status) {
        filtered = filtered.filter(task => task.status === this.localFilter.status)
      }

      // Priority filter
      if (this.localFilter.priority) {
        filtered = filtered.filter(task => task.priority === this.localFilter.priority)
      }

      return filtered
    },
    totalRows() {
      return this.filteredTasks.length
    },
  },
  methods: {
    avatarText,
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        in_progress: 'info',
        review: 'primary',
        completed: 'success',
      }
      return variants[status] || 'secondary'
    },
    getStatusLabel(status) {
      const labels = {
        pending: 'قيد الانتظار',
        in_progress: 'قيد التنفيذ',
        review: 'قيد المراجعة',
        completed: 'مكتملة',
      }
      return labels[status] || status
    },
    getPriorityVariant(priority) {
      const variants = {
        low: 'success',
        medium: 'info',
        high: 'warning',
        urgent: 'danger',
      }
      return variants[priority?.toLowerCase()] || 'secondary'
    },
    async changeStatus(task, newStatus) {
      const oldStatus = task.status
      task.status = newStatus

      try {
        await this.$store.dispatch('tasks/updateTaskStatus', {
          taskId: task.id,
          status: newStatus,
        })

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'تم تحديث الحالة',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'تم تحديث حالة المهمة بنجاح',
          },
        })

        this.$emit('refresh')
      } catch (error) {
        task.status = oldStatus
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'خطأ',
            icon: 'XIcon',
            variant: 'danger',
            text: 'فشل تحديث حالة المهمة',
          },
        })
      }
    },
    onSearchChange() {
      // Emit search change if needed
      this.$emit('filter-change', this.localFilter)
    },
    onFilterChange() {
      this.$emit('filter-change', this.localFilter)
    },
    clearFilters() {
      this.localFilter = {
        search: '',
        status: null,
        priority: null,
      }
      this.$emit('filter-change', this.localFilter)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';

.tasks-list-view {
  .table {
    th {
      font-weight: 600;
    }
  }
}
</style>
